/**
 * AG-UI Chat Core Library
 *
 * A DOM-agnostic core library for AG-UI chat functionality.
 * Provides state management, agent communication, and event system
 * that can be used independently or with custom renderers.
 *
 * @example
 * // Use with default SDC renderer (automatic)
 * // Just include the component and it works
 *
 * @example
 * // Use programmatically with custom rendering
 * const chat = AguiChat.getInstance('#my-chat');
 * chat.on('message:added', (message) => myCustomRenderer(message));
 * chat.sendMessage('Hello!');
 */

import {
  HttpAgent,
  randomUUID,
  type AgentSubscriber,
} from '@ag-ui/client';
import { type Message } from '@ag-ui/core';
import type { JsonPatchOperation } from '../utils/state';
import { StateManager, type StateChangeListener } from '../utils/state';
import { TokenManager, createAuthHeaders } from '../utils/token';
import { HttpResponseError, formatErrorMessage } from '../utils/errors';
import { isHitlToolCall, parseHitlRequest, createHitlRequestFromToolCall, type HitlRequest, type HitlResponse } from '../utils/hitl';
import { toolRegistry, type ChatCoreInterface } from '../utils/tools';
import { createDebugLogger, type DebugLogger } from '../utils/debug';

/**
 * Configuration options for AguiChatCore.
 */
export interface AguiChatCoreConfig {
  /** API endpoint for the chat agent. When runnerEndpoint is set, this becomes the SSE streaming endpoint. Supports {threadId} and {runId} placeholders. */
  endpoint: string;
  /** Optional runner endpoint for POSTing messages (e.g., Drupal controller). When set, messages are POSTed here while SSE events come from the main endpoint. */
  runnerEndpoint?: string;
  /** Agent ID to chat with */
  agentId?: string;
  /** Optional token endpoint for authentication */
  tokenEndpoint?: string;
  /** Enable debug logging */
  debug?: boolean;
  /** Initial thread ID (defaults to random UUID) */
  threadId?: string;
  /** Maximum number of messages to send with each request (default: 10) */
  maxMessages?: number;
  /** Timeout in milliseconds for the SSE stream. If no events are received within this period, the stream is aborted and the chat is reset. Set to 0 to disable. (default: 30000) */
  streamTimeout?: number;
}

/**
 * Event types emitted by the chat core.
 */
/**
 * Suggestion pill definition.
 */
export interface SuggestionPill {
  /** Display text shown on the pill button. */
  title: string;
  /** Message text sent when the pill is clicked. */
  message: string;
}

export type AguiChatEventType =
  | 'ready'
  | 'chat:cleared'
  | 'message:added'
  | 'message:updated'
  | 'message:finalized'
  | 'run:started'
  | 'run:finished'
  | 'run:stopped'
  | 'run:error'
  | 'tool:started'
  | 'tool:args'
  | 'tool:ended'
  | 'tool:result'
  | 'hitl:request'
  | 'hitl:response'
  | 'state:snapshot'
  | 'state:delta'
  | 'indicator:show'
  | 'indicator:hide'
  | 'pills:updated'
  | 'auth:started'
  | 'auth:completed'
  | 'auth:failed';

/**
 * Indicator types for status display.
 */
export type IndicatorType = 'connecting' | 'thinking' | 'processing' | 'tool-call';

/**
 * Event payload types.
 */
export interface AguiChatEvents {
  'ready': undefined;
  'chat:cleared': undefined;
  'message:added': { message: Message; isUser: boolean };
  'message:updated': { messageId: string; content: string };
  'message:finalized': { messageId: string; content: string };
  'run:started': undefined;
  'run:finished': undefined;
  'run:stopped': { messageId: string | null };
  'run:error': { message: string; code?: string };
  'tool:started': { toolCallId: string; toolName: string; hasCustomRenderer: boolean; isHitl: boolean };
  'tool:args': { toolCallId: string; args: Record<string, unknown> };
  'tool:ended': { toolCallId: string; toolName: string; args: Record<string, unknown> };
  'tool:result': { toolCallId: string; result: unknown };
  'hitl:request': { request: HitlRequest };
  'hitl:response': { response: HitlResponse; request: HitlRequest };
  'state:snapshot': { snapshot: unknown };
  'state:delta': { delta: JsonPatchOperation[] };
  'indicator:show': { type: IndicatorType; id?: string; text?: string };
  'indicator:hide': { type: IndicatorType; id?: string };
  'pills:updated': { pills: SuggestionPill[] };
  'auth:started': undefined;
  'auth:completed': undefined;
  'auth:failed': { error: unknown };
}

type EventCallback<T extends AguiChatEventType> = (payload: AguiChatEvents[T]) => void;

/**
 * Core chat functionality - DOM agnostic.
 * Manages state, agent communication, and emits events for rendering.
 * Implements ChatCoreInterface for tool integration.
 */
export class AguiChatCore implements ChatCoreInterface {
  private config: AguiChatCoreConfig;
  private agent: HttpAgent;
  private tokenManager: TokenManager | null = null;
  private stateManager: StateManager;
  private isReady = false;
  private isRunning = false;

  // Event system
  private eventListeners = new Map<AguiChatEventType, Set<EventCallback<AguiChatEventType>>>();

  // Message state tracking
  private currentMessageId = '';
  private currentMessageContent = '';

  // Text message buffer for SSE streaming (accumulates delta content)
  private sseTextMessageBuffer = '';

  // Per-toolCallId accumulated args buffers for SSE/runner mode (delta strings)
  private sseToolCallArgsBuffers = new Map<string, Record<string, unknown>>();

  // AbortController for cancelling an in-progress SSE stream (runner mode)
  private sseAbortController: AbortController | null = null;

  // HITL tracking
  private pendingHitlToolCall: {
    toolCallId: string;
    toolName: string;
    args: Record<string, unknown>;
  } | null = null;

  // Custom rendered tools tracking
  private customRenderedTools = new Set<string>();

  // Suggestion pills
  private pills: SuggestionPill[] = [];

  // Debug logging (timestamped, no-ops unless config.debug is enabled)
  private debugLogger: DebugLogger = createDebugLogger(() => this.config.debug ?? false);

  constructor(config: AguiChatCoreConfig) {
    this.config = config;

    // Initialize token manager if endpoint is configured
    if (config.tokenEndpoint) {
      this.tokenManager = new TokenManager({
        tokenEndpoint: config.tokenEndpoint,
      });
    }

    // Initialize state manager
    this.stateManager = new StateManager({
      debug: config.debug,
      onStateUpdate: (delta) => this.handleOutgoingStateUpdate(delta),
    });

    // Connect state manager to tool registry
    this.stateManager.subscribe((state, event, previousState) => {
      toolRegistry.handleStateEvent(state, event, previousState);
    });

    // Set up tool registry callbacks
    toolRegistry.setStateUpdateCallback((delta) => this.handleOutgoingStateUpdate(delta));
    toolRegistry.setSendMessageCallback((message) => this.sendMessage(message));

    // Register this core instance with the tool registry for direct access
    toolRegistry.setActiveChatCore(this);

    // Initialize the HttpAgent
    this.agent = this.createAgent();
  }

  /**
   * Creates and configures the HttpAgent.
   * When runnerEndpoint is set, the agent URL is used only for SSE streaming.
   */
  private createAgent(): HttpAgent {
    const agentUrl = new URL(this.config.endpoint, window.location.origin);
    if (this.config.agentId) {
      agentUrl.searchParams.set('agentId', this.config.agentId);
    }

    return new HttpAgent({
      url: agentUrl.toString(),
      agentId: this.config.agentId,
      threadId: this.config.threadId || randomUUID(),
    });
  }

  /**
   * Replaces placeholders in the endpoint URL with actual values.
   * Supports {threadId} and {runId} placeholders.
   */
  private buildStreamingUrl(threadId: string, runId: string): string {
    let url = this.config.endpoint;
    url = url.replace('{threadId}', encodeURIComponent(threadId));
    url = url.replace('{runId}', encodeURIComponent(runId));
    return url;
  }

  /**
   * Connects to the SSE streaming endpoint and processes events.
   * Resolves when RUN_FINISHED or RUN_ERROR is received, or when the stream closes.
   *
   * @param streamUrl - The SSE endpoint URL
   * @param threadId - Optional thread ID for fetching thread-specific token (Mercure private topics)
   */
  private async listenToStream(streamUrl: string, threadId?: string): Promise<void> {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
    };

    // Add auth headers if available
    // For thread-specific subscriptions (Mercure), get a token with thread claims
    if (this.tokenManager) {
      const token = threadId
        ? await this.tokenManager.getTokenForThread(threadId)
        : await this.tokenManager.getToken();
      if (token) {
        Object.assign(headers, createAuthHeaders(token));
      }
    }

    this.debugLogger.log('AG-UI: Connecting to SSE stream:', streamUrl);

    const subscriber = this.createSubscriber();

    // Track when the run is complete so we can exit early
    let runComplete = false;

    // Wrap the subscriber to detect run completion
    const wrappedSubscriber: AgentSubscriber = {
      ...subscriber,
      onRunFinishedEvent: (args) => {
        subscriber.onRunFinishedEvent?.(args);
        runComplete = true;
      },
      onRunErrorEvent: (args) => {
        subscriber.onRunErrorEvent?.(args);
        runComplete = true;
      },
    };

    let response: Response;
    try {
      response = await fetch(streamUrl, {
        method: 'GET',
        headers,
        signal: this.sseAbortController?.signal,
      });
    } catch (error) {
      // AbortError is expected when stopCurrentRun() cancels the fetch
      if (error instanceof DOMException && error.name === 'AbortError') {
        this.debugLogger.log('AG-UI: SSE fetch aborted by stopCurrentRun()');
        return;
      }
      throw error;
    }

    if (!response.ok) {
      throw new HttpResponseError(response.status, `Stream endpoint returned ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Configurable stream timeout.
    const timeoutMs = this.config.streamTimeout ?? AguiChatCore.DEFAULT_STREAM_TIMEOUT;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let timedOut = false;

    // Resets the inactivity timer. Called each time data arrives.
    const resetTimeout = () => {
      if (timeoutMs <= 0) return;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        this.debugLogger.warn(`AG-UI: Stream timed out after ${timeoutMs}ms of inactivity`);
        timedOut = true;
        // Abort the fetch to break out of reader.read().
        this.sseAbortController?.abort();
      }, timeoutMs);
    };

    try {
      // Start the initial timeout window.
      resetTimeout();

      while (!runComplete) {
        let result: ReadableStreamReadResult<Uint8Array>;
        try {
          result = await reader.read();
        } catch (error) {
          // reader.read() throws when the stream is cancelled via AbortController
          if (error instanceof DOMException && error.name === 'AbortError') {
            if (timedOut) {
              throw new Error('The response timed out. Please try again.');
            }
            this.debugLogger.log('AG-UI: SSE stream read aborted by stopCurrentRun()');
            return;
          }
          throw error;
        }

        const { done, value } = result;
        if (done) break;

        // Data received – reset the inactivity timer.
        resetTimeout();

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events (lines ending with \n\n)
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const eventStr of events) {
          if (!eventStr.trim()) continue;
          this.processSSEEvent(eventStr, wrappedSubscriber);
          // Check if run completed during processing
          if (runComplete) break;
        }
      }

      // Process any remaining buffer if we haven't completed
      if (!runComplete && buffer.trim()) {
        this.processSSEEvent(buffer, wrappedSubscriber);
      }

      this.debugLogger.log('AG-UI: SSE stream finished, runComplete:', runComplete);
    } finally {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      reader.releaseLock();
    }
  }

  /**
   * Processes a single SSE event string.
   */
  private processSSEEvent(eventStr: string, subscriber: AgentSubscriber): void {
    const lines = eventStr.split('\n');
    let data = '';

    for (const line of lines) {
      if (line.startsWith('data:')) {
        data += line.slice(5).trim();
      }
    }

    if (!data) return;

    try {
      const event = JSON.parse(data);

      // Build the event wrapper that subscriber methods expect
      const eventWrapper = { event } as Parameters<NonNullable<AgentSubscriber['onEvent']>>[0];

      // Call generic onEvent first
      subscriber.onEvent?.(eventWrapper);

      // Map event types to subscriber methods
      const handlerMap: Record<string, keyof AgentSubscriber> = {
        'RUN_STARTED': 'onRunStartedEvent',
        'RUN_FINISHED': 'onRunFinishedEvent',
        'RUN_ERROR': 'onRunErrorEvent',
        'TEXT_MESSAGE_START': 'onTextMessageStartEvent',
        'TEXT_MESSAGE_CONTENT': 'onTextMessageContentEvent',
        'TEXT_MESSAGE_END': 'onTextMessageEndEvent',
        'TOOL_CALL_START': 'onToolCallStartEvent',
        'TOOL_CALL_ARGS': 'onToolCallArgsEvent',
        'TOOL_CALL_END': 'onToolCallEndEvent',
        'TOOL_CALL_RESULT': 'onToolCallResultEvent',
        'STATE_SNAPSHOT': 'onStateSnapshotEvent',
        'STATE_DELTA': 'onStateDeltaEvent',
      };

      const handlerName = handlerMap[event.type as string];
      if (handlerName) {
        const handler = subscriber[handlerName] as ((arg: unknown) => void) | undefined;
        handler?.(eventWrapper);
      }
    } catch (error) {
      this.debugLogger.warn('AG-UI: Failed to parse SSE event:', data, error);
    }
  }


  /**
   * Initializes the chat (authentication, etc.).
   * Call this after setting up event listeners.
   */
  async initialize(): Promise<void> {
    if (this.tokenManager) {
      try {
        this.emit('auth:started', undefined);
        await this.tokenManager.getToken();
        this.emit('auth:completed', undefined);
      } catch (error) {
        this.emit('auth:failed', { error });
      }
    }
    this.isReady = true;
    this.emit('ready', undefined);
  }

  // ============================================
  // Public API - Methods for external use
  // ============================================

  /**
   * Sends a user message and runs the agent.
   */
  async sendMessage(text: string): Promise<void> {
    if (!text.trim() || !this.isReady || this.isRunning) {
      return;
    }

    const userMessage: Message = {
      id: randomUUID(),
      role: 'user',
      content: text.trim(),
    };

    this.agent.addMessage(userMessage);
    this.emit('message:added', { message: userMessage, isUser: true });

    await this.runAgent();
  }

  /**
   * Clears the chat and starts a fresh session.
   * Creates a new thread ID so the backend (e.g., Agno) starts fresh memory.
   *
   * @param clearUI - Whether to also clear the UI messages (default: true)
   */
  clearChat(clearUI = true): void {
    // Stop any in-progress generation before clearing
    if (this.isRunning) {
      this.stopCurrentRun();
    }

    // Create new agent with fresh thread ID for backend session reset
    this.agent = this.createAgent();
    this.resetMessageState();
    this.customRenderedTools.clear();

    // Emit event so renderers can clear their UI
    if (clearUI) {
      this.emit('chat:cleared', undefined);
    }
  }

  /**
   * Gets the current state.
   */
  getState<T = unknown>(): T {
    return this.stateManager.getState<T>();
  }

  /**
   * Updates the shared state (sends to agent).
   */
  updateState(delta: JsonPatchOperation[]): void {
    this.stateManager.handleDelta(delta);
    this.handleOutgoingStateUpdate(delta);
  }

  /**
   * Sets a value in the state.
   */
  setState(path: string, value: unknown): void {
    this.stateManager.setValue(path, value);
  }

  /**
   * Gets the current suggestion pills.
   */
  getPills(): SuggestionPill[] {
    return [...this.pills];
  }

  /**
   * Sets the suggestion pills and notifies renderers.
   */
  setPills(pills: SuggestionPill[]): void {
    this.pills = [...pills];
    this.emit('pills:updated', { pills: this.pills });
  }

  /**
   * Checks if the chat is ready.
   */
  get ready(): boolean {
    return this.isReady;
  }

  /**
   * Checks if the agent is currently running.
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Gets the current thread ID.
   */
  get threadId(): string {
    return this.agent.threadId || '';
  }

  /**
   * Switches to a different conversation thread.
   * The backend (Agno) will recall the conversation history for this thread.
   *
   * @param newThreadId - The thread ID to switch to
   * @param clearUI - Whether to clear the UI messages (default: true)
   */
  switchThread(newThreadId: string, clearUI = true): void {
    // Stop any in-progress generation before switching
    if (this.isRunning) {
      this.stopCurrentRun();
    }

    // Create new agent with the specified thread ID
    const agentUrl = new URL(this.config.endpoint, window.location.origin);
    if (this.config.agentId) {
      agentUrl.searchParams.set('agentId', this.config.agentId);
    }

    this.agent = new HttpAgent({
      url: agentUrl.toString(),
      agentId: this.config.agentId,
      threadId: newThreadId,
    });

    this.resetMessageState();
    this.customRenderedTools.clear();

    // Emit event so renderers can update
    if (clearUI) {
      this.emit('chat:cleared', undefined);
    }

    // Emit a specific event for thread switch
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('agui:thread:switched', {
        detail: { threadId: newThreadId }
      }));
    }
  }

  /**
   * Stops the currently running agent generation.
   *
   * Cancels both HttpAgent (direct mode) and SSE fetch (runner mode),
   * finalizes any in-progress message, hides indicators, and emits `run:stopped`.
   */
  stopCurrentRun(): void {
    if (!this.isRunning) return;

    // Capture the current message ID before resetting state
    const stoppedMessageId = this.currentMessageId || null;

    // 1. Abort SSE stream (runner mode)
    if (this.sseAbortController) {
      this.sseAbortController.abort();
      this.sseAbortController = null;
    }

    // 2. Abort HttpAgent (direct mode) — safe to call even if not running
    try {
      this.agent.abortRun();
    } catch {
      // abortRun may throw if no run is active — ignore
    }

    // 3. Finalize any partially-streamed message so it remains visible
    if (this.currentMessageId && this.currentMessageContent) {
      this.finalizeMessage(this.currentMessageContent);
    }

    // 4. Clean up state
    this.isRunning = false;
    this.customRenderedTools.clear();
    this.sseToolCallArgsBuffers.clear();
    this.resetMessageState();

    // 5. Hide all indicators
    this.emit('indicator:hide', { type: 'connecting' });
    this.emit('indicator:hide', { type: 'thinking' });
    this.emit('indicator:hide', { type: 'processing' });

    // 6. Emit stopped event (distinct from finished)
    this.emit('run:stopped', { messageId: stoppedMessageId });
  }

  // ============================================
  // Event System
  // ============================================

  /**
   * Subscribes to an event.
   */
  on<T extends AguiChatEventType>(event: T, callback: EventCallback<T>): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback as EventCallback<AguiChatEventType>);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribes from an event.
   */
  off<T extends AguiChatEventType>(event: T, callback: EventCallback<T>): void {
    this.eventListeners.get(event)?.delete(callback as EventCallback<AguiChatEventType>);
  }

  /**
   * Emits an event to all listeners.
   */
  private emit<T extends AguiChatEventType>(event: T, payload: AguiChatEvents[T]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in ${event} listener:`, error);
        }
      });
    }

    // Also dispatch DOM event for external listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(`agui:${event}`, { detail: payload }));
    }
  }

  /**
   * Subscribes to state changes.
   */
  onStateChange(listener: StateChangeListener): () => void {
    return this.stateManager.subscribe(listener);
  }

  // ============================================
  // HITL (Human-in-the-Loop) Support
  // ============================================

  /**
   * Responds to a HITL request.
   */
  async respondToHitl(response: HitlResponse, request: HitlRequest): Promise<void> {
    this.emit('hitl:response', { response, request });

    if (request.toolName) {
      // Tool-based HITL - add tool result message
      const toolResultMessage: Message = {
        id: randomUUID(),
        role: 'tool',
        content: response.result,
        toolCallId: response.toolCallId,
      } as Message;

      this.agent.addMessage(toolResultMessage);
      this.emit('indicator:show', { type: 'processing' });

      // Continue agent run
      await this.runAgent();
    } else {
      // Legacy state-based HITL
      const responseState = {
        hitl_response: {
          request_id: request.id,
          tool_call_id: response.toolCallId,
          approved: response.approved,
          result: response.result,
        },
      };
      this.agent.setState(responseState);
      this.emit('indicator:show', { type: 'processing' });
    }
  }

  // ============================================
  // Internal Methods
  // ============================================

  /**
   * Default maximum messages to keep in history.
   */
  private static readonly DEFAULT_MAX_MESSAGES = 10;

  /**
   * Default stream timeout in milliseconds (30 seconds).
   */
  private static readonly DEFAULT_STREAM_TIMEOUT = 30000;

  /**
   * Trims the message history to respect maxMessages config.
   * Ensures message integrity by:
   * 1. Always keeping at least the most recent user message
   * 2. Preserving tool call / tool result pairs (tool results must follow their tool call)
   * 3. Not breaking assistant -> tool result chains
   */
  private trimMessages(): void {
    const maxMessages = this.config.maxMessages ?? AguiChatCore.DEFAULT_MAX_MESSAGES;

    // 0 or negative means unlimited
    if (maxMessages <= 0) return;

    const messages = this.agent.messages;
    if (messages.length <= maxMessages) return;

    // Start from the end and work backwards to find a safe trim point
    // We want to keep maxMessages, but we need to ensure we don't break:
    // 1. Tool call / tool result pairs
    // 2. The sequence integrity (user -> assistant -> tool -> assistant)

    let trimIndex = messages.length - maxMessages;

    // Walk forward from trim point to find a safe boundary
    // A safe boundary is at a user message or at the start of a complete exchange
    while (trimIndex < messages.length) {
      const msg = messages[trimIndex];

      // User message is always a safe starting point
      if (msg.role === 'user') {
        break;
      }

      // Tool result without its preceding context is invalid
      // Assistant with tool calls needs the following tool results
      // Keep walking forward until we find a user message
      trimIndex++;
    }

    // If we couldn't find a safe point, just keep everything
    if (trimIndex >= messages.length) {
      return;
    }

    // Trim messages
    const trimmedMessages = messages.slice(trimIndex);
    this.agent.setMessages(trimmedMessages);

    this.debugLogger.log(`AG-UI: Trimmed messages from ${messages.length} to ${trimmedMessages.length}`);
  }

  /**
   * Runs the agent with event subscription.
   * When runnerEndpoint is configured:
   *   1. POST messages to runnerEndpoint
   *   2. Listen to SSE stream from endpoint (with placeholders replaced)
   * Otherwise, uses standard HttpAgent flow.
   */
  private async runAgent(): Promise<void> {
    if (this.isRunning) return;

    this.isRunning = true;
    this.resetMessageState();

    // Shown immediately, independent of any network response - covers the
    // gap a delayed or buffering proxy would otherwise leave blank before
    // RUN_STARTED (which drives the 'thinking' indicator) ever arrives.
    this.emit('indicator:show', { type: 'connecting' });

    try {
      // Trim message history before sending
      this.trimMessages();

      if (this.config.runnerEndpoint) {
        // Runner endpoint mode: POST to runner, then listen to SSE stream
        // Token is fetched with threadId in runAgentWithRunner
        await this.runAgentWithRunner();
      } else {
        // Standard mode: use HttpAgent directly
        // Update auth headers if needed
        if (this.tokenManager) {
          const token = await this.tokenManager.getToken();
          if (token) {
            Object.assign(this.agent.headers, createAuthHeaders(token));
          }
        }
        const subscriber = this.createSubscriber();
        // Build forwardedProps with user_id if available from token response
        const forwardedProps: Record<string, unknown> = {};
        if (this.tokenManager) {
          const userId = this.tokenManager.getUserId();
          if (userId) {
            forwardedProps.user_id = userId;
          }
        }
        await this.agent.runAgent({ forwardedProps }, subscriber);
      }
    } catch (error) {
      console.error('Error running agent:', error);
      // Don't emit run:error for user-initiated stops -- stopCurrentRun() already handles cleanup
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        this.emit('run:error', { message: formatErrorMessage(error) });

        // Reset the chat session on stream errors so the user can start fresh.
        this.clearChat(false);
      }
    } finally {
      this.isRunning = false;
      this.sseAbortController = null;
    }
  }

  /**
   * Runs the agent using the runner endpoint mode.
   * First subscribes to SSE stream, then posts to runner endpoint.
   * This ensures we don't miss the RUN_STARTED event.
   */
  private async runAgentWithRunner(): Promise<void> {
    // Emit run started early
    this.emit('run:started', undefined);
    this.emit('indicator:hide', { type: 'connecting' });
    this.emit('indicator:show', { type: 'thinking' });

    // Generate threadId and runId upfront so we can subscribe before posting
    const threadId = this.agent.threadId || randomUUID();
    const runId = randomUUID();

    // Create AbortController for this run so stopCurrentRun() can cancel it
    this.sseAbortController = new AbortController();

    try {
      // 1. Build streaming URL with placeholders replaced
      const streamUrl = this.buildStreamingUrl(threadId, runId);

      this.debugLogger.log('AG-UI: Subscribing to SSE stream first:', streamUrl);

      // 2. Start listening to SSE stream FIRST (non-blocking)
      // Pass threadId so we can get a token with Mercure subscribe claims
      const streamPromise = this.listenToStream(streamUrl, threadId);

      // 3. Small delay to ensure SSE connection is established before posting
      // This is necessary because EventSource connection is async
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 4. POST to runner endpoint with the same threadId/runId
      this.debugLogger.log('AG-UI: Posting to runner endpoint with threadId:', threadId, 'runId:', runId);
      await this.postToRunnerWithIds(this.agent.messages, threadId, runId);

      // 5. Wait for the stream to complete
      await streamPromise;
    } catch (error) {
      // Emit error and rethrow
      this.emit('indicator:hide', { type: 'thinking' });
      this.emit('indicator:hide', { type: 'processing' });
      throw error;
    }
  }

  /**
   * Posts messages to the runner endpoint with pre-generated threadId and runId.
   */
  private async postToRunnerWithIds(messages: Message[], threadId: string, runId: string): Promise<void> {
    if (!this.config.runnerEndpoint) {
      throw new Error('Runner endpoint not configured');
    }

    const runnerUrl = new URL(this.config.runnerEndpoint, window.location.origin);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add auth headers if available - use threadId to share cached token with SSE
    if (this.tokenManager) {
      const token = await this.tokenManager.getTokenForThread(threadId);
      if (token) {
        Object.assign(headers, createAuthHeaders(token));
      }
    }

    const body = {
      agentId: this.config.agentId,
      threadId,
      runId,
      messages: messages.map((m) => {
        const msg = m as Message & { toolCallId?: string };
        return {
          id: msg.id,
          role: msg.role,
          content: msg.content,
          ...(msg.toolCallId ? { toolCallId: msg.toolCallId } : {}),
        };
      }),
      state: this.stateManager.getState(),
    };

    this.debugLogger.log('AG-UI: Posting to runner endpoint:', runnerUrl.toString(), body);

    const response = await fetch(runnerUrl.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // Read the body for console/debug logging only - it must never reach
      // the rendered chat transcript (see HttpResponseError/formatErrorMessage).
      const errorText = await response.text();
      throw new HttpResponseError(response.status, `Runner endpoint returned ${response.status}`, errorText);
    }
  }


  /**
   * Creates the event subscriber for the agent.
   */
  private createSubscriber(): AgentSubscriber {
    return {
      onEvent: ({ event }) => {
        if (event.type !== 'TEXT_MESSAGE_CONTENT') {
          this.debugLogger.log('Agent event:', event.type, event);
        }
      },

      onRunStartedEvent: () => {
        this.emit('run:started', undefined);
        this.emit('indicator:hide', { type: 'connecting' });
        this.emit('indicator:show', { type: 'thinking' });
      },

      onTextMessageStartEvent: ({ event }) => {
        this.emit('indicator:hide', { type: 'thinking' });
        this.emit('indicator:hide', { type: 'processing' });
        this.resetMessageState();
        this.currentMessageId = event.messageId || randomUUID();

        // Emit message:added immediately so renderers can create the element
        const message: Message = {
          id: this.currentMessageId,
          role: 'assistant',
          content: '',
        };
        this.emit('message:added', { message, isUser: false });
      },

      onTextMessageContentEvent: ({ event, textMessageBuffer }) => {
        // Standard mode provides textMessageBuffer, runner mode provides event.delta
        if (textMessageBuffer) {
          this.handleTextContent(textMessageBuffer);
        } else if (event.delta) {
          // Accumulate delta for runner mode
          this.sseTextMessageBuffer += event.delta;
          this.handleTextContent(this.sseTextMessageBuffer);
        }
      },

      onTextMessageEndEvent: ({ textMessageBuffer }) => {
        // Standard mode provides textMessageBuffer, runner mode uses accumulated buffer
        const finalContent = textMessageBuffer || this.sseTextMessageBuffer;

        // In runner mode, we need to manually add the assistant message to the agent's history
        // Standard mode handles this internally in HttpAgent.runAgent()
        if (this.config.runnerEndpoint && this.currentMessageId && finalContent) {
          const assistantMessage: Message = {
            id: this.currentMessageId,
            role: 'assistant',
            content: finalContent,
          };
          this.agent.addMessage(assistantMessage);
        }

        this.finalizeMessage(finalContent);
      },

      onToolCallStartEvent: ({ event }) => {
        const isHitl = isHitlToolCall(event.toolCallName);
        const hasCustomRenderer = toolRegistry.hasRenderer(event.toolCallName);

        if (isHitl) {
          this.pendingHitlToolCall = {
            toolCallId: event.toolCallId,
            toolName: event.toolCallName,
            args: {},
          };
          this.emit('indicator:hide', { type: 'thinking' });
        } else if (hasCustomRenderer) {
          this.customRenderedTools.add(event.toolCallId);
          this.emit('indicator:hide', { type: 'thinking' });
        }

        this.emit('tool:started', {
          toolCallId: event.toolCallId,
          toolName: event.toolCallName,
          hasCustomRenderer,
          isHitl,
        });
      },

      onToolCallArgsEvent: ({ event, partialToolCallArgs }) => {
        // Standard mode provides partialToolCallArgs, runner mode extracts from event.
        // In runner/SSE mode the args arrive as a JSON string in event.delta, not event.args.
        const eventWithDelta = event as typeof event & { args?: Record<string, unknown>; delta?: string };
        let args: Record<string, unknown> = partialToolCallArgs || eventWithDelta.args || {};

        if (!partialToolCallArgs && eventWithDelta.delta) {
          // Runner mode: parse the delta string and merge into the buffer.
          try {
            const parsed = JSON.parse(eventWithDelta.delta) as Record<string, unknown>;
            const existing = this.sseToolCallArgsBuffers.get(event.toolCallId) ?? {};
            const merged = { ...existing, ...parsed };
            this.sseToolCallArgsBuffers.set(event.toolCallId, merged);
            args = merged;
          } catch {
            // Partial/streaming fragment — keep existing buffer as-is.
            args = this.sseToolCallArgsBuffers.get(event.toolCallId) ?? {};
          }
        }

        if (this.pendingHitlToolCall?.toolCallId === event.toolCallId) {
          this.pendingHitlToolCall.args = args;
        }
        this.emit('tool:args', { toolCallId: event.toolCallId, args });
      },

      onToolCallEndEvent: ({ event, toolCallName, toolCallArgs }) => {
        // Standard mode provides toolCallName/toolCallArgs, runner mode extracts from event
        const eventWithTool = event as typeof event & { toolCallName?: string; toolCallArgs?: Record<string, unknown> };
        const name = toolCallName || eventWithTool.toolCallName || '';
        // In runner mode, use the accumulated buffer as the final args.
        const args = toolCallArgs || eventWithTool.toolCallArgs
          || this.sseToolCallArgsBuffers.get(event.toolCallId)
          || {};
        // Clean up the per-call buffer.
        this.sseToolCallArgsBuffers.delete(event.toolCallId);

        if (this.pendingHitlToolCall?.toolCallId === event.toolCallId) {
          const hitlRequest = createHitlRequestFromToolCall(
            event.toolCallId,
            name,
            args,
          );
          this.emit('hitl:request', { request: hitlRequest });
          this.pendingHitlToolCall = null;
        }

        this.emit('tool:ended', {
          toolCallId: event.toolCallId,
          toolName: name,
          args,
        });

        // Show processing indicator if no more tool calls
        this.emit('indicator:show', { type: 'processing' });
      },

      onToolCallResultEvent: ({ event }) => {
        this.emit('tool:result', { toolCallId: event.toolCallId, result: event.content });
      },

      onRunFinishedEvent: () => {
        this.emit('indicator:hide', { type: 'thinking' });
        this.emit('indicator:hide', { type: 'processing' });
        this.customRenderedTools.clear();
        this.sseToolCallArgsBuffers.clear();
        this.emit('run:finished', undefined);
      },

      onRunErrorEvent: ({ event }) => {
        this.emit('indicator:hide', { type: 'thinking' });
        this.emit('indicator:hide', { type: 'processing' });
        this.customRenderedTools.clear();
        this.sseToolCallArgsBuffers.clear();
        this.emit('run:error', { message: event.message || 'An error occurred', code: event.code });
      },

      onStateSnapshotEvent: ({ event }) => {
        this.stateManager.handleSnapshot(event.snapshot);
        this.emit('state:snapshot', { snapshot: event.snapshot });

        // Legacy HITL pattern support
        const hitlRequest = parseHitlRequest(event.snapshot);
        if (hitlRequest) {
          this.emit('hitl:request', { request: hitlRequest });
        }
      },

      onStateDeltaEvent: ({ event }) => {
        this.stateManager.handleDelta(event.delta);
        this.emit('state:delta', { delta: event.delta });
      },
    };
  }

  /**
   * Handles incoming text content from the stream.
   */
  private handleTextContent(textMessageBuffer: string): void {

    this.currentMessageContent = textMessageBuffer;
    this.emit('message:updated', {
      messageId: this.currentMessageId,
      content: textMessageBuffer,
    });
  }

  /**
   * Finalizes the current message.
   */
  private finalizeMessage(textMessageBuffer: string): void {
    this.emit('message:finalized', {
      messageId: this.currentMessageId,
      content: textMessageBuffer,
    });
    this.resetMessageState();
  }

  /**
   * Resets the current message state.
   */
  private resetMessageState(): void {
    this.currentMessageId = '';
    this.currentMessageContent = '';
    this.sseTextMessageBuffer = '';
  }

  /**
   * Handles outgoing state updates to the agent.
   */
  private handleOutgoingStateUpdate(delta: JsonPatchOperation[]): void {
    const currentState = this.stateManager.getState<Record<string, unknown>>();

    this.debugLogger.log('Sending state update to agent:', { delta, currentState });

    this.agent.setState(currentState);

    window.dispatchEvent(new CustomEvent('agui:state:update', {
      detail: { delta, state: currentState },
    }));
  }
}

// ============================================
// Instance Management
// ============================================

const instances = new Map<string | HTMLElement, AguiChatCore>();

/**
 * Gets or creates an AguiChatCore instance.
 */
export function getInstance(target: string | HTMLElement): AguiChatCore | undefined {
  if (typeof target === 'string') {
    const element = document.querySelector(target);
    if (element) {
      return instances.get(element as HTMLElement);
    }
    return instances.get(target);
  }
  return instances.get(target);
}

/**
 * Registers an instance.
 */
export function registerInstance(target: string | HTMLElement, instance: AguiChatCore): void {
  instances.set(target, instance);
}

/**
 * Unregisters an instance.
 */
export function unregisterInstance(target: string | HTMLElement): void {
  instances.delete(target);
}

export { type HitlRequest, type HitlResponse };
